import React, { useState } from 'react';
import { format } from 'date-fns';
import type { Payslip } from './payrollTypes';
import { formatINR } from '@/shared/utils/currency';

interface PrintablePayslipProps {
  payslip: Payslip;
}

export const PrintablePayslip: React.FC<PrintablePayslipProps> = ({ payslip }) => {
  // Capture a stable "generated" fallback timestamp once at mount so render stays
  // pure (used only when the payslip has no persisted created_at).
  const [generatedFallback] = useState(() => Date.now());
  const totalEarnings = payslip.earnings?.reduce((sum, item) => sum + item.amount, 0) || 0;
  const totalDeductions = payslip.deductions?.reduce((sum, item) => sum + item.amount, 0) || 0;

  return (
    <div className="p-8 bg-card text-black min-h-[500px] font-sans rounded-md" id={`payslip-print-${payslip.id}`}>
      <div className="text-center border-b-2 border-on-surface pb-4 mb-6">
        <h1 data-document className="text-headline-sm font-semibold uppercase tracking-widest text-on-surface">ACME CORP</h1>
        <p className="text-body-md text-on-surface-variant">Payslip for the month of {payslip.month_year}</p>
      </div>

      <div className="grid grid-cols-2 gap-8 mb-8">
        <div>
          <p className="font-semibold text-headline-sm text-on-surface">{payslip.user?.display_name}</p>
          <p className="text-body-md text-on-surface-variant">Employee ID: {payslip.user?.id?.substring(0, 8)}</p>
          <p className="text-body-md text-on-surface-variant">Role: {payslip.user?.position || payslip.user?.department}</p>
        </div>
        <div className="text-right">
          <p className="text-body-md text-on-surface-variant">Status: <span className="font-semibold uppercase">{payslip.status}</span></p>
          <p className="text-body-md text-on-surface-variant">Generated: {format(new Date(payslip.created_at || generatedFallback), 'dd MMM yyyy')}</p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-8 mb-8">
        {/* Earnings */}
        <div>
          <h3 className="font-semibold border-b border-border-strong pb-2 mb-4 text-success-text">Earnings</h3>
          <ul className="space-y-2">
            {payslip.earnings?.map((e, i) => (
              <li key={i} className="flex justify-between text-body-md">
                <span>{e.label}</span>
                <span className="font-mono">{formatINR(e.amount)}</span>
              </li>
            ))}
          </ul>
          <div className="flex justify-between font-semibold border-t border-border-strong mt-4 pt-2">
            <span>Total Earnings</span>
            <span className="font-mono text-success-text">{formatINR(totalEarnings)}</span>
          </div>
        </div>

        {/* Deductions */}
        <div>
          <h3 className="font-semibold border-b border-border-strong pb-2 mb-4 text-danger-text">Deductions</h3>
          <ul className="space-y-2">
            {payslip.deductions?.map((d, i) => (
              <li key={i} className="flex justify-between text-body-md">
                <span>{d.label}</span>
                <span className="font-mono">{formatINR(d.amount)}</span>
              </li>
            ))}
          </ul>
          <div className="flex justify-between font-semibold border-t border-border-strong mt-4 pt-2">
            <span>Total Deductions</span>
            <span className="font-mono text-danger-text">{formatINR(totalDeductions)}</span>
          </div>
        </div>
      </div>

      <div className="bg-surface-variant p-4 flex justify-between items-center border border-border-strong">
        <span className="text-headline-sm font-semibold text-on-surface">Net Salary Payable</span>
        <span className="text-data-lg font-semibold font-mono text-success-text">{formatINR(payslip.net_salary)}</span>
      </div>

      <div className="mt-20 flex justify-between px-4">
        <div className="text-center text-body-md text-on-surface-variant">
          <div className="w-48 border-t border-border-strong mb-2 mx-auto"></div>
          Employer Signature
        </div>
        <div className="text-center text-body-md text-on-surface-variant">
          <div className="w-48 border-t border-border-strong mb-2 mx-auto"></div>
          Employee Signature
        </div>
      </div>

      <style dangerouslySetInnerHTML={{__html: `
        @media print {
          body * { visibility: hidden; }
          #payslip-print-${payslip.id}, #payslip-print-${payslip.id} * { visibility: visible; }
          #payslip-print-${payslip.id} { position: absolute; left: 0; top: 0; width: 100%; height: 100%; padding: 40px; margin: 0; box-sizing: border-box; }
        }
      `}} />
    </div>
  );
};
